import {
  EditorView,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  ViewPlugin,
  Decoration,
  keymap
} from '@codemirror/view';
import type { DecorationSet, ViewUpdate } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import type { Range } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';

export interface LrcEditorCallbacks {
  onChange?: (value: string) => void;
  onCursorChange?: (row: number, col: number) => void;
}

export interface LrcEditor {
  getValue(): string;
  setValue(text: string): void;
  setWordWrap(enabled: boolean): void;
  setFontSize(size: 'sm' | 'md' | 'lg'): void;
  wrapSelection(before: string, after: string): void;
  focus(): void;
  destroy(): void;
}

// ---------------------------------------------------------------------------
// Syntax Highlighting Tokens
// ---------------------------------------------------------------------------
const tsBracketOpenDeco = Decoration.mark({ class: 'cm-lrc-ts-bracket' });
const tsTimeDeco = Decoration.mark({ class: 'cm-lrc-ts-time' });
const tsBracketCloseDeco = Decoration.mark({ class: 'cm-lrc-ts-bracket cm-lrc-ts-bracket-close' });
const metaBracketDeco = Decoration.mark({ class: 'cm-lrc-meta-bracket' });
const metaTagDeco = Decoration.mark({ class: 'cm-lrc-meta-tag' });
const metaValDeco = Decoration.mark({ class: 'cm-lrc-meta-val' });
const metaValSkyDeco = Decoration.mark({ class: 'cm-lrc-meta-val-sky' });

/**
 * Builds live decorations for visible lines in the viewport for instant speed.
 */
function buildLrcDecorations(view: EditorView): DecorationSet {
  const widgets: Range<Decoration>[] = [];

  for (const { from, to } of view.visibleRanges) {
    let lineObj = view.state.doc.lineAt(from);
    let lineNum = lineObj.number;
    const endLineNum = view.state.doc.lineAt(to).number;

    while (lineNum <= endLineNum) {
      const line = view.state.doc.line(lineNum);
      const lineText = line.text;
      const lineStart = line.from;

      // Check for Metadata ID Tags (SS2): e.g. [ti:Song Title] or [length: 04:47.37]
      const metaMatch = lineText.match(/^(\s*\[)([a-zA-Z]{2,10}):(.*?)(\]\s*)$/);
      if (metaMatch && metaMatch[1] && metaMatch[2] && metaMatch[3] !== undefined && metaMatch[4]) {
        const bracket1From = lineStart + lineText.indexOf(metaMatch[1]);
        const bracket1To = bracket1From + metaMatch[1].length;
        const tagFrom = bracket1To;
        const tagTo = tagFrom + metaMatch[2].length + 1; // includes ':'
        const valFrom = tagTo;
        const valTo = valFrom + metaMatch[3].length;
        const bracket2From = valTo;
        const bracket2To = bracket2From + metaMatch[4].length;

        widgets.push(metaBracketDeco.range(bracket1From, bracket1To));
        widgets.push(metaTagDeco.range(tagFrom, tagTo));
        const isSky = metaMatch[2].toLowerCase() === 'length' || metaMatch[2].toLowerCase() === 'offset';
        widgets.push((isSky ? metaValSkyDeco : metaValDeco).range(valFrom, valTo));
        widgets.push(metaBracketDeco.range(bracket2From, bracket2To));
      } else {
        // Timestamps Check (SS1): highlight all [mm:ss.xx] with laser-focused digits & optical cushion
        const tsRegex = /\[(\d{1,2}:\d{2}(?:\.\d{1,3})?)\]/g;
        let match: RegExpExecArray | null;

        while ((match = tsRegex.exec(lineText)) !== null) {
          const start = lineStart + match.index;
          const end = start + match[0].length;
          widgets.push(tsBracketOpenDeco.range(start, start + 1));
          widgets.push(tsTimeDeco.range(start + 1, end - 1));
          widgets.push(tsBracketCloseDeco.range(end - 1, end));
        }
      }
      lineNum++;
    }
  }

  return Decoration.set(widgets, true);
}

const lrcHighlightPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildLrcDecorations(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildLrcDecorations(update.view);
      }
    }
  },
  {
    decorations: (v) => v.decorations
  }
);

// ---------------------------------------------------------------------------
// Studio Theme Definitions
// ---------------------------------------------------------------------------
const baseStudioTheme = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: '#0b0f19',
    color: '#f8fafc',
    fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
  },
  '.cm-scroller': {
    overflow: 'auto',
    fontFamily: 'inherit'
  },
  '.cm-content': {
    padding: '16px 18px',
    caretColor: '#38bdf8'
  },
  '.cm-line': {
    padding: '2px 0',
    lineHeight: 'inherit'
  },
  '&.cm-focused .cm-cursor': {
    borderLeftColor: '#38bdf8',
    borderLeftWidth: '2px'
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'rgba(56, 189, 248, 0.35) !important'
  },
  '.cm-gutters': {
    backgroundColor: '#090d15',
    color: '#64748b',
    borderRight: '1px solid rgba(51, 65, 85, 0.45)',
    paddingRight: '4px'
  },
  '.cm-gutterElement': {
    padding: '0 10px 0 14px',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    userSelect: 'none'
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(56, 189, 248, 0.045)'
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'rgba(56, 189, 248, 0.08)',
    color: '#38bdf8',
    fontWeight: '700'
  }
});

function getFontSizeTheme(size: 'sm' | 'md' | 'lg') {
  if (size === 'sm') {
    return EditorView.theme({
      '&': { fontSize: '12.5px', lineHeight: '22px' }
    });
  }
  if (size === 'lg') {
    return EditorView.theme({
      '&': { fontSize: '15.5px', lineHeight: '29px' }
    });
  }
  return EditorView.theme({
    '&': { fontSize: '14px', lineHeight: '26px' }
  });
}

let activeEditorInstance: LrcEditor | null = null;

export function getActiveEditor(): LrcEditor | null {
  return activeEditorInstance;
}

export function getEditorValue(): string {
  return activeEditorInstance ? activeEditorInstance.getValue() : '';
}

export function setEditorValue(text: string): void {
  activeEditorInstance?.setValue(text);
}

// ---------------------------------------------------------------------------
// Editor Factory
// ---------------------------------------------------------------------------
export function createLrcEditor(
  container: HTMLElement,
  callbacks?: LrcEditorCallbacks
): LrcEditor {
  const wrapCompartment = new Compartment();
  const fontCompartment = new Compartment();

  const changeListener = EditorView.updateListener.of((update: ViewUpdate) => {
    if (update.docChanged && callbacks?.onChange) {
      callbacks.onChange(update.state.doc.toString());
    }
    if ((update.docChanged || update.selectionSet) && callbacks?.onCursorChange) {
      const head = update.state.selection.main.head;
      const line = update.state.doc.lineAt(head);
      const row = line.number;
      const col = head - line.from + 1;
      callbacks.onCursorChange(row, col);
    }
  });

  const state = EditorState.create({
    doc: '',
    extensions: [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      lrcHighlightPlugin,
      baseStudioTheme,
      wrapCompartment.of([]),
      fontCompartment.of(getFontSizeTheme('md')),
      changeListener
    ]
  });

  const view = new EditorView({
    state,
    parent: container
  });

  const editor: LrcEditor = {
    getValue(): string {
      return view.state.doc.toString();
    },
    setValue(text: string): void {
      const current = view.state.doc.toString();
      if (current === text) return;
      view.dispatch({
        changes: { from: 0, to: current.length, insert: text }
      });
    },
    setWordWrap(enabled: boolean): void {
      view.dispatch({
        effects: wrapCompartment.reconfigure(enabled ? EditorView.lineWrapping : [])
      });
    },
    setFontSize(size: 'sm' | 'md' | 'lg'): void {
      view.dispatch({
        effects: fontCompartment.reconfigure(getFontSizeTheme(size))
      });
    },
    wrapSelection(before: string, after: string): void {
      const selection = view.state.selection.main;
      const selectedText = view.state.sliceDoc(selection.from, selection.to);
      const replacement = `${before}${selectedText}${after}`;
      const newCursorPos = selectedText.length > 0
        ? selection.from + replacement.length
        : selection.from + before.length;
      view.dispatch({
        changes: { from: selection.from, to: selection.to, insert: replacement },
        selection: { anchor: newCursorPos }
      });
      view.focus();
    },
    focus(): void {
      view.focus();
    },
    destroy(): void {
      if (activeEditorInstance === editor) {
        activeEditorInstance = null;
      }
      view.destroy();
    }
  };

  activeEditorInstance = editor;
  return editor;
}
